import { useState, useEffect } from 'react';
import axios from 'axios';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Header from '../Header';
import { usePermissions } from '../context/PermissionsContext';
import Sidebar from '../Sidebar';
import { useNavigate, Link } from 'react-router-dom';
import watermarkImage from '../assets/coverzone-watermark.png';

const API_URL = process.env.REACT_APP_API_URL;

interface Client {
  id: number;
  name?: string;
  middle_names?: string;
  surname?: string;
  nickname?: string;
  date_of_birth?: string;
  gender?: string;
  marital_status?: string;
  status?: string;
  language?: string;
  id_passport_number?: string;
  email?: string;
  emails?: string[];
  mobile_number?: string | null;
  telephone_number?: string;
  fax_number?: string;
  drivers_license_codes?: string;
  tax_number?: string;
  employer_name?: string;
  job_description?: string;
  consultant?: string;
  address?: string | null;
  personal?: string;
  commercial?: boolean;
  client_number?: string;
  organization?: string;
}

interface Consultant {
  id: number;
  name: string;
  surname: string;
  email?: string;
}

interface DriverLicenseEntry {
  code: string;
  dateOfFirstIssue: string;
  expiresOn: string;
}

const Clients = () => {
  const { can } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<{ id: number; email: string; username?: string; organization?: string; surname?: string; client_number?: string } | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    name: '',
    middle_names: '',
    surname: '',
    nickname: '',
    date_of_birth: '',
    gender: '',
    marital_status: '',
    status: 'Draft',
    language: '',
    id_passport_number: '',
    emails: [] as string[],
    mobile_number: '+27',
    organization: '',
    mobile_no: '',
    telephone_number: '+27',
    tax_number: '',
    employer_name: '',
    job_description: '',
    consultant: '',
    address: '',
    personal: '',
    commercial: false,
    client_number: '',
    drivers_licenses: [] as DriverLicenseEntry[],
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [telephoneError, setTelephoneError] = useState<string | null>(null);
  const [idPassportError, setIdPassportError] = useState<string | null>(null);
  // state for available license codes
  const [availableLicenseCodes, setAvailableLicenseCodes] = useState([
    'A', 'A1', 'B', 'C', 'C1', 'D', 'D1', 'EB', 'EC', 'EC1', 'ED', 'ED1'
  ]);

  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addEmail = () => {
  const trimmedEmail = emailInput.trim();
  
  if (!trimmedEmail) {
    setEmailError('Email cannot be empty');
    return;
  }
  
  if (!validateEmail(trimmedEmail)) {
    setEmailError('Please enter a valid email address');
    return;
  }
  
  if (formData.emails.includes(trimmedEmail)) {
    setEmailError('Email already added');
    return;
  }
  
  setFormData(prev => ({
    ...prev,
    emails: [...prev.emails, trimmedEmail]
  }));
  
  setEmailInput('');
  setEmailError(null);
};

const removeEmail = (index: number) => {
  setFormData(prev => ({
    ...prev,
    emails: prev.emails.filter((_, i) => i !== index)
  }));
};

const handleEmailKeyDown = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ',') {
    e.preventDefault();
    addEmail();
  }
};

  // Function to fetch languages
const fetchLanguages = async () => {
  setLoadingLanguages(true);
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/api/languages`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    
    // Handle the API response structure
    if (response.data.success && Array.isArray(response.data.data)) {
      setLanguages(response.data.data);
    } else {
      console.error('Unexpected languages response structure:', response.data);
      setLanguages([]);
    }
  } catch (error) {
    console.error('Error fetching languages:', error);
    setLanguages([]);
  } finally {
    setLoadingLanguages(false);
  }
};

  const [languages, setLanguages] = useState<{ id: number; name: string; is_default: boolean }[]>([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  const [brokerDetails, setBrokerDetails] = useState<{ companyName: string } | null>(null);

  const fetchBrokerDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/broker-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      console.log('Broker details raw response:', response.data);
      
      if (response.data.success && response.data.data) {
        console.log('Broker details data:', response.data.data);
        
        // Handle both camelCase and snake_case responses
        const data = response.data.data;
        const companyName = data.companyName || data.company_name;
        
        console.log('Company name:', companyName);
        console.log('First 3 letters:', companyName?.substring(0, 3).toUpperCase());
        
        // Store with camelCase for consistency
        setBrokerDetails({
          companyName: companyName
        });
      } else if (Array.isArray(response.data) && response.data.length > 0) {
        console.log('Broker details array:', response.data[0]);
        const data = response.data[0];
        const companyName = data.companyName || data.company_name;
        setBrokerDetails({
          companyName: companyName
        });
      } else {
        console.log('No broker details found in response');
      }
    } catch (error) {
      console.error('Error fetching broker details:', error);
    }
  };

  const getAvailableLicenseCodes = (currentIndex: number) => {
    // Get all license codes currently selected in THIS form
    const currentSelectedCodes = formData.drivers_licenses
      .map(license => license.code)
      .filter(code => code !== '');
    
    // Filter available licenses: remove licenses already selected in other dropdowns
    return availableLicenseCodes.filter(code => {
      const selectedInOtherDropdowns = currentSelectedCodes.filter(
        (selectedCode, index) => index !== currentIndex && selectedCode === code
      );
      return selectedInOtherDropdowns.length === 0;
    });
  };

  const canAddMoreLicenses = () => {
    const availableCodes = getAvailableLicenseCodes(formData.drivers_licenses.length);
    return availableCodes.length > 0;
  };

  const navigate = useNavigate();

  const loadDefaultClients = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/api/clients?limit=10`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    
    console.log('📦 Clients API Response:', response.data);
    
    // ✅ Handle different response formats
    let clientsData: Client[] = [];
    
    if (Array.isArray(response.data)) {
      clientsData = response.data;
    } else if (response.data && typeof response.data === 'object') {
      if (Array.isArray(response.data.data)) {
        clientsData = response.data.data;
      } else if (Array.isArray(response.data.clients)) {
        clientsData = response.data.clients;
      } else if (Array.isArray(response.data.results)) {
        clientsData = response.data.results;
      } else {
        const values = Object.values(response.data);
        if (values.length > 0 && Array.isArray(values[0])) {
          clientsData = values[0];
        } else {
          console.error('❌ Unknown response format:', response.data);
          clientsData = [];
        }
      }
    }
    
    console.log('✅ Processed clients data:', clientsData.length, 'clients');
    setFilteredClients(clientsData);
    setError(null);
  } catch (err) {
    setError('Failed to load default clients');
    console.error('❌ Default clients error:', err);
    setFilteredClients([]);
  } finally {
    setLoading(false);
  }
};

  // function to check ALL clients
  const checkAllClients = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/api/clients?limit=1000`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    //console.log('=== ALL EXISTING CLIENTS ===');
    response.data.forEach((client: Client, index: number) => {
      //console.log(`${index + 1}. ID: ${client.id}, Email: ${client.email || 'No email'}, Name: ${client.name || 'No name'} ${client.surname || ''}`);
    });
    //console.log(`=== TOTAL CLIENTS: ${response.data.length} ===`);
  } catch (error) {
    //console.error('Error fetching clients:', error);
  }
};

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        //console.log('API Response (User):', response.data);
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
        localStorage.removeItem('token');
        navigate('/login');
      }
    };

    const fetchConsultants = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/consultants`, {  // ← CORRECT ENDPOINT
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Handle the API response structure
        if (response.data.success && Array.isArray(response.data.data)) {
          setConsultants(response.data.data);
        } else {
          console.error('Unexpected response structure:', response.data);
          setConsultants([]);
        }
      } catch (error) {
        console.error('Error fetching consultants:', error);
        setConsultants([]);
      }
    };

    fetchUser();
    loadDefaultClients();
    fetchConsultants();
    fetchLanguages();
    fetchBrokerDetails();
    checkAllClients();
  }, [navigate]);

  useEffect(() => {
    if (brokerDetails?.companyName) {
      const prefix = brokerDetails.companyName.substring(0, 3).toUpperCase();
      // Don't set client_number here - we'll generate it fresh in handleSubmit
      console.log('Broker prefix available:', prefix);
    }
  }, [brokerDetails]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
  const searchValue = e.target.value.toLowerCase();
  setSearchTerm(searchValue);

  if (searchValue === '') {
    // If search term is empty, show all clients (or a default set)
    loadDefaultClients();
    return;
  }

  // Filter the existing clients in memory
  const filtered = filteredClients.filter(client =>
    (client.name?.toLowerCase().includes(searchValue) ||
    client.surname?.toLowerCase().includes(searchValue) ||
    client.email?.toLowerCase().includes(searchValue) ||
    client.mobile_number?.toLowerCase().includes(searchValue))
  );
  
  setFilteredClients(filtered);
  setError(filtered.length === 0 ? 'No matches found' : null);
};

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Failed to load watermark image:', e);
  };

  const handleImageLoad = (type: string) => {
    //console.log(`${type} image loaded successfully`);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
  const { name, value, type } = e.target;

  if (name === 'id_passport_number') {
    // Remove non-digit characters
    const cleanedValue = value.replace(/[^0-9]/g, '');
    // Check if the input is exactly 13 digits
    if (cleanedValue.length !== 13 && cleanedValue.length > 0) {
      setIdPassportError('ID/Passport number must be exactly 13 digits');
    } else {
      setIdPassportError(null);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: cleanedValue,
    }));
  } else if (name === 'mobile_number') {
    // Handle mobile number - ensure +27 is always preserved
    let cleanedValue = value;
    
    // If the value doesn't start with +27, force it to start with +27
    if (!cleanedValue.startsWith('+27')) {
      // Remove any existing +27 or 27 and start fresh with +27
      cleanedValue = '+27' + cleanedValue.replace(/^(\+27|27)/, '').replace(/[^0-9]/g, '');
    } else {
      // If it already has +27, just clean the remaining digits
      cleanedValue = '+27' + cleanedValue.slice(3).replace(/[^0-9]/g, '');
    }
    
    // Validate length (10 digits after +27)
    const digitsOnly = cleanedValue.slice(3);
    if (digitsOnly.length > 10) {
      setMobileError('Mobile number cannot exceed 10 digits');
      return;
    } else {
      setMobileError(null);
    }
    
    console.log('📱 Mobile number updated:', cleanedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: cleanedValue,
    }));
  } else if (name === 'telephone_number') {
    // Handle telephone number similarly
    let cleanedValue = value;
    
    if (!cleanedValue.startsWith('+27')) {
      cleanedValue = '+27' + cleanedValue.replace(/^(\+27|27)/, '').replace(/[^0-9]/g, '');
    } else {
      cleanedValue = '+27' + cleanedValue.slice(3).replace(/[^0-9]/g, '');
    }
    
    const digitsOnly = cleanedValue.slice(3);
    if (digitsOnly.length > 10) {
      setTelephoneError('Telephone number cannot exceed 10 digits');
      return;
    } else {
      setTelephoneError(null);
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: cleanedValue,
    }));
  } else if (name === 'language') {
    // For language dropdown, we want to store the language name, not ID
    const selectedLanguage = languages.find(lang => lang.id.toString() === value);
    setFormData((prev) => ({
      ...prev,
      [name]: selectedLanguage ? selectedLanguage.name : value,
    }));
  } else {
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  }
};

  const addDriverLicense = () => {
  const availableCodes = getAvailableLicenseCodes(formData.drivers_licenses.length);
  const availableCode = availableCodes[0] || '';
  
  setFormData(prev => ({
    ...prev,
    drivers_licenses: [
      ...prev.drivers_licenses,
      { code: availableCode, dateOfFirstIssue: '', expiresOn: '' }
    ]
  }));
};

const removeDriverLicense = (index: number) => {
  setFormData(prev => ({
    ...prev,
    drivers_licenses: prev.drivers_licenses.filter((_, i) => i !== index)
  }));
};

const handleDriverLicenseChange = (index: number, field: keyof DriverLicenseEntry, value: string) => {
  setFormData(prev => ({
    ...prev,
    drivers_licenses: prev.drivers_licenses.map((license, i) => 
      i === index ? { ...license, [field]: value } : license
    )
  }));
};

          const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setFormError(null);

  if (mobileError || idPassportError) {
    setFormError('Please correct the errors in the form.');
    return;
  }

  // Validate emails
  if (formData.emails.length === 0) {
    setFormError('Please add at least one email address.');
    return;
  }

  // Validate all emails
  for (const email of formData.emails) {
    if (!validateEmail(email)) {
      setFormError(`Invalid email format: ${email}`);
      return;
    }
  }

  // Validate driver's licenses
  const invalidLicenses = formData.drivers_licenses.filter(
    license => license.code && (!license.dateOfFirstIssue || !license.expiresOn)
  );

  if (invalidLicenses.length > 0) {
    setFormError('Please provide both Date of First Issue and Expires On for all selected license codes.');
    return;
  }

  if (formData.title && !['Mr', 'Mrs', 'Miss'].includes(formData.title)) {
    setFormError('Please select a valid Title.');
    setLoading(false);
    return;
  }

  if (formData.gender && !['Male', 'Female', 'Other'].includes(formData.gender)) {
    setFormError('Please select a valid gender (Male, Female, or Other).');
    setLoading(false);
    return;
  }

  try {
    const token = localStorage.getItem('token');

    // Get the prefix from broker details
    const prefix = brokerDetails?.companyName 
      ? brokerDetails.companyName.substring(0, 3).toUpperCase() 
      : 'MNH'; // Fallback prefix

    console.log('Using prefix:', prefix);

    // Fetch all clients to find the highest sequence number for our NEW format (prefix + 5 digits)
    const clientsResponse = await axios.get(`${API_URL}/api/clients?limit=1000`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // Filter clients that have client_number matching our NEW format: prefix + 5 digits
    // This regex looks for prefix followed by exactly 5 digits
    const newFormatRegex = new RegExp(`^${prefix}\\d{5}$`);
    
    const clientsWithNewFormat = clientsResponse.data.filter((client: Client) => 
      client.client_number && newFormatRegex.test(client.client_number)
    );

    console.log('Clients with new format (prefix + 5 digits):', clientsWithNewFormat.length);

    // Find the highest sequence number from the new format clients
    let maxSequence = 0;
    clientsWithNewFormat.forEach((client: Client) => {
      if (client.client_number) {
        // Extract the 5-digit number part after the prefix
        const numberPart = client.client_number.substring(prefix.length);
        const sequenceNum = parseInt(numberPart, 10);
        console.log(`Client ${client.client_number}: sequenceNum=${sequenceNum}`);
        if (!isNaN(sequenceNum) && sequenceNum > maxSequence) {
          maxSequence = sequenceNum;
        }
      }
    });

    console.log('Max sequence found in new format:', maxSequence);

    // Increment for new client and pad with zeros to 5 digits
    const nextSequence = maxSequence + 1;
    const paddedSequence = nextSequence.toString().padStart(5, '0');
    
    // Construct the client number: prefix + 5-digit padded sequence
    const newClientNumber = `${prefix}${paddedSequence}`;

    console.log('New client number:', newClientNumber);

    // Prepare emails for submission
    const emailString = formData.emails.join(',');
    const primaryEmail = formData.emails[0];

    // Create a clean submission object
    const submissionData = {
      title: formData.title,
      name: formData.name,
      middle_names: formData.middle_names,
      surname: formData.surname,
      nickname: formData.nickname,
      date_of_birth: formData.date_of_birth,
      gender: formData.gender,
      marital_status: formData.marital_status,
      status: formData.status,
      language: formData.language,
      id_passport_number: formData.id_passport_number,
      emails: formData.emails,
      email: primaryEmail,
      email_string: emailString,
      mobile_number: formData.mobile_number,
      telephone_number: formData.telephone_number,
      tax_number: formData.tax_number,
      employer_name: formData.employer_name,
      job_description: formData.job_description,
      consultant: formData.consultant,
      address: formData.address,
      personal: formData.personal,
      commercial: formData.commercial,
      // Use the new sequential client number
      client_number: newClientNumber,
      drivers_license_codes: formData.drivers_licenses
        .filter(license => license.code)
        .map(license => `${license.code}|${license.dateOfFirstIssue}|${license.expiresOn}`)
        .join(',')
    };

    console.log('📤 Final submissionData:', submissionData);

    const response = await axios.post(`${API_URL}/api/clients`, submissionData, {
      headers: { Authorization: `Bearer ${token}` },
    });

    console.log('✅ Client created:', response.data);

    // Refresh the client list
const refreshResponse = await axios.get(`${API_URL}/api/clients?limit=10`, {
  headers: { Authorization: `Bearer ${token}` },
});

// ✅ Handle different response formats
let clientsData: Client[] = [];
if (Array.isArray(refreshResponse.data)) {
  clientsData = refreshResponse.data;
} else if (refreshResponse.data && Array.isArray(refreshResponse.data.data)) {
  clientsData = refreshResponse.data.data;
} else {
  clientsData = [];
}
setFilteredClients(clientsData);
    
  } catch (err) {
    if (axios.isAxiosError(err)) {
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      
      if (err.response?.status === 400) {
        const errorDetail = err.response.data.detail;
        setFormError(`Database error: ${errorDetail}`);
      } else {
        setFormError('Failed to add client. Please try again.');
      }
    } else {
      setFormError('Failed to add client. Please try again.');
    }
    console.error('Add client error:', err);
  }
};

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src={watermarkImage}
          alt="Watermark"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-100 h-100 opacity-5 z-0 select-none"
          onError={handleImageError}
          onLoad={() => handleImageLoad('Watermark')}
        />
      </div>

      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Clients Search Page"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />

        <div className="flex-1 p-6">
          <div className="max-w-2xl mx-auto mb-8 flex items-center gap-4">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search clients by name..."
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition-all"
              />
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>

            {can('add_client', 'write') && (
              <button
                onClick={() => navigate('/add-client')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add New Client
              </button>
            )}

          </div>

          <div className="bg-white rounded-lg shadow-md p-6 w-full">
            {loading && <p className="text-center text-gray-500">Loading...</p>}
            {error && <p className="text-center text-red-500">{error}</p>}
            {!loading && filteredClients.length === 0 && !error && (
              <p className="text-center text-gray-500">No clients found.</p>
            )}
            
            {Array.isArray(filteredClients) && filteredClients.length > 0 && (
  <div className="overflow-y-auto max-h-[500px]">
                <table className="w-full">
                  <thead className="sticky top-0 bg-gray-100 z-10 shadow-lg">
                    <tr>
                      <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Client No.</th>
                      <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">First Name</th>
                      <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Email</th>
                      <th className="px-4 py-2 text-left text-gray-600 whitespace-nowrap">Mobile</th>
                      <th className="px-4 py-2 text-center text-gray-600 whitespace-nowrap">Personal</th>
                      <th className="px-4 py-2 text-center text-gray-600 whitespace-nowrap">Commercial</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredClients.map((client) => {
                      // Just display the client_number directly without adding prefix again
                      const displayNumber = client.client_number || '-';
                      
                      return (
                        <tr key={client.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap font-mono text-sm text-gray-600">
                            {displayNumber}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <Link
                              to={`/personal/${client.id}`}
                              className="text-blue-600 hover:underline"
                            >
                              {client.name || 'Unknown'} {client.surname || ''}
                            </Link>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">{client.email || '-'}</td>
                          <td className="px-4 py-2 whitespace-nowrap">{client.mobile_number || '-'}</td>
                          <td className="px-4 py-2 text-center whitespace-nowrap">
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                              client.personal ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {client.personal ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center whitespace-nowrap">
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                              client.commercial ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {client.commercial ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                </table>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Clients;